import { useEffect, useSyncExternalStore } from 'react';

import type { ScreenManifest } from '@/shared/screen';

import { screens } from '../screens';
import { hasSensitiveSegment, personalKey, readJson, removeByPrefix, usePersonalScope, writeJson } from './personal-storage';

// Recentes da Início (LGPD): as últimas telas e registros abertos, em memória e no `sessionStorage` da aba,
// por correspondente e usuário, apagados no logout (`clearRecents`, chamado pelo `clearSessionCache`).
// Guarda só o id da tela, o caminho (com o id interno do registro, nunca a query) e uma referência curta
// de uma lista de permissão por tela. O nome do registro que a trilha mostra (titular, tomador, e-mail)
// nunca entra. Arquivo na lista de permissão de armazenamento do `eslint.config.js`.

/** Prefixo da chave: `uyzy.recents.<tenant>.<sub>`. */
export const RECENTS_PREFIX = 'uyzy.recents.';
export const MAX_RECENTS = 8;

export interface RecentEntry {
  /** Id da tela no registro (`screens.ts`). */
  readonly screen: string;
  /** Só o caminho: a query (filtros, busca por nome ou CPF) fica de fora. */
  readonly href: string;
  /** Referência não sensível ("#39242"), só nas telas da lista de permissão. */
  readonly ref?: string;
  readonly at: number;
}

const SKIP = new Set(['home', 'underConstruction']);
/** Rota de criação ("nova operação"): não é um registro para voltar. */
const CREATE = new Set(['nova', 'novo']);

/**
 * Referência por tela, e só destas: o número da operação (`#39242`, a trilha do detalhe). Conta seria
 * "Conta digital" e os 4 últimos dígitos no máximo; o detalhe da conta não publica o número na trilha
 * (publica o titular), então fica só "Conta digital".
 */
const REF_RULES: Readonly<Record<string, RegExp>> = {
  'creditNote.detail': /^#\d{1,9}$/,
};

export function recentRef(screenId: string, crumb: string | undefined): string | undefined {
  const rule = REF_RULES[screenId];
  const text = crumb?.trim();
  return rule && text && rule.test(text) ? text : undefined;
}

/** A entrada de uma tela aberta, ou `undefined` quando ela não entra nos recentes. */
export function recentEntryOf(screen: ScreenManifest | undefined, pathname: string, crumb: string | undefined, at: number): RecentEntry | undefined {
  if (!screen || SKIP.has(screen.id) || screen.path.startsWith('/dev')) return undefined;
  const href = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (!href.startsWith('/') || href.includes('?') || href.includes('#')) return undefined;
  if (href.split('/').some((segment) => CREATE.has(segment))) return undefined;
  if (hasSensitiveSegment(href)) return undefined;
  const ref = recentRef(screen.id, crumb);
  return { screen: screen.id, href, ...(ref ? { ref } : {}), at };
}

type Lookup = { byId(id: string): ScreenManifest | undefined; byPath(path: string): ScreenManifest | undefined };

/** Lista de permissão na volta do armazenamento: forma, tela registrada no mesmo caminho, sem dado pessoal. */
export function sanitizeRecents(raw: unknown, registry: Lookup = screens): RecentEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: RecentEntry[] = [];
  for (const item of raw) {
    if (out.length === MAX_RECENTS) break;
    if (item === null || typeof item !== 'object') continue;
    const { screen: id, href, ref, at } = item as Record<string, unknown>;
    if (typeof id !== 'string' || typeof href !== 'string' || typeof at !== 'number' || !Number.isFinite(at)) continue;
    const screen = registry.byId(id);
    if (!screen || registry.byPath(href)?.id !== id) continue;
    const entry = recentEntryOf(screen, href, typeof ref === 'string' ? ref : undefined, at);
    if (entry && entry.href === href && !out.some((other) => other.href === href)) out.push(entry);
  }
  return out;
}

export const recentsKey = (tenant: string | undefined, user: string | undefined) => personalKey(RECENTS_PREFIX, tenant, user);

const sessionStore = (): Storage | undefined => {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
};

const EMPTY: readonly RecentEntry[] = [];
const memory = new Map<string, readonly RecentEntry[]>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function readRecents(key: string | undefined, storage = sessionStore): readonly RecentEntry[] {
  if (!key) return EMPTY;
  const cached = memory.get(key);
  if (cached) return cached;
  const entries = sanitizeRecents(readJson(storage, key));
  memory.set(key, entries);
  return entries;
}

/**
 * Põe no topo, sem repetir o caminho. A referência acompanha a trilha da tela montada: chega quando o
 * registro carrega e troca junto com ele (a da tela anterior nunca fica presa).
 */
export function pushRecent(key: string | undefined, entry: RecentEntry, storage = sessionStore): void {
  if (!key) return;
  const current = readRecents(key, storage);
  const top = current[0];
  if (top && top.href === entry.href && top.screen === entry.screen && top.ref === entry.ref) return;
  const next = sanitizeRecents([entry, ...current.filter((item) => item.href !== entry.href)]);
  memory.set(key, next);
  writeJson(storage, key, next);
  notify();
}

/** Logout (LGPD): esvazia a memória e apaga os recentes de toda pessoa da aba. */
export function clearRecents(storage: Storage | undefined = sessionStore()): void {
  memory.clear();
  removeByPrefix(storage, RECENTS_PREFIX);
  notify();
}

export function useRecents(): readonly RecentEntry[] {
  const { tenant, user } = usePersonalScope();
  const key = recentsKey(tenant, user);
  return useSyncExternalStore(
    subscribe,
    () => readRecents(key),
    () => EMPTY,
  );
}

/** Ponto único que alimenta os recentes: a tela montada pela casca e a referência da trilha. */
export function useRecentsFeed(screen: ScreenManifest | undefined, pathname: string, crumb: string | undefined) {
  const { tenant, user } = usePersonalScope();
  const key = recentsKey(tenant, user);
  useEffect(() => {
    const entry = recentEntryOf(screen, pathname, crumb, Date.now());
    if (entry) pushRecent(key, entry);
  }, [key, screen, pathname, crumb]);
}
